import { ComponentStateTypes } from "components/common/ComponentState";
import Form from "components/common/form/Form";
import CheckboxGroupInput from "components/common/form/inputs/CheckboxGroupInput";
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";
import Input from "components/common/form/inputs/Input";
import FormActions from "components/common/form/partials/FormActions";
import Page from "components/common/page/Page";
import { api, graph } from "config/axios-context";
import { AppRoutes } from "config/router";
import { useEffect,  useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { DropdownInputPropsType } from "types/form-types";
import { Statuses, UserFormDocument, UserFormQuery, UserFormQueryVariables } from "types/graphql";
import { onlyUnique, Roles } from "utilities/app-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import useAppNavigation from "utilities/hooks/useAppNavigate";

type UserFormType = {
    roles: string[],
    state: Statuses,
    name: string,
    username: string
}

const UserFormPage = () => {
    const params = useParams<{id: string}>();
    const [t] = useTranslation();
    const navigate = useAppNavigation();
    const context = useAppContext();
    const [model, setModel] = useState<UserFormType>({
        roles: [],
        name: '',
        username: '',
        state: Statuses.Inactive
    });
    const [roles, setRoles] = useState<string[]>([]);
    const [state, setState] = useState<ComponentStateTypes>('loading');

    const stateDropdown: DropdownInputPropsType<Statuses> = {
        dataSource: {
            data: [
                Statuses.Active,
                Statuses.Inactive
            ],
            search: 'off'
        },
        getItemId: i => i,
        view: {
            inputView: i => t('status.' + i)
        },
        customErrors: [{
            message: "",
            name: 'user-form-page-req',
            validate(value, input) {
                return value !== Statuses.PaddingApproval;
            },
        }]
    }

    useEffect(() => {
        loadData().then();
    }, []);

    const loadData = async() => {
        setState('loading');
        try {
            const response = await graph<UserFormQuery, UserFormQueryVariables>({
                query: UserFormDocument,
                variables: {
                    id: Number(params.id)
                }
            });

            if(response.errors) throw response.errors;

            if(!response.data?.user) {
                setState('error');
                return;
            }

            setRoles(response.data!.allRoles! as string[]);
            setModel({
                roles: response.data!.user!.roles!.items!.map(c => c?.role) as string[],
                state: response.data!.user!.status,
                name: response!.data!.user!.name,
                username: response!.data!.user!.username,
            })
            setState('');
        } catch (error) {
            setState('error');
            throw error;
        }
    }

    const onRoleChange = (value: string[] | undefined, event: React.ChangeEvent<HTMLInputElement>, current: string) => {
        if(!value) {
            setModel({
                ...model,
                roles: []
            });
        } else {
            var checked = event.target.checked;
            if(current === Roles.ADMIN && checked) {
                value = [...roles];
            }
            if(checked && current.endsWith('.Write')) {
                    const readRole = current.replace('.Write', '.Read');
                    value.push(readRole);
            } else if (!checked && current.endsWith('.Read')) {
                const readRole = current.replace('.Read', '.Write');
                value = value.filter(c => c !== readRole);
            }
    
            setModel({
                ...model,
                roles: [...value.filter(onlyUnique)]
            })
        }
    }
       
    const onSubmit = async () => {
        const requestData = {
            id: Number(params.id),
            roles: model.roles,
            state: model.state
        }

        const response = await api.post('/user/update', requestData);
        if(response.success) {
            navigate(AppRoutes.USER_VIEW, {id: params.id!});
        } else {
            context.notify('error', 'form.submit.error');
        }
    }

    return (
        <Page title="pages.users.edit" state={state}>
            <Form name="user-form-page" onSubmit={onSubmit}>
                <fieldset>
                    <Input 
                        label="fields.name.name"
                        value={model.name}
                        readonly
                    />
                    <Input 
                        label="fields.username.name"
                        value={model.username}
                        readonly
                    />
                </fieldset>
                <fieldset>
                    <CheckboxGroupInput
                        data={roles}
                        value={model.roles}
                        onChange={onRoleChange}
                        display={i => i + " "}
                        required
                        label="fields.roles.name"
                        bulkChange={v => setModel({...model, roles: v})}
                    />
                </fieldset>
                <fieldset>
                    <DropdownInput 
                        {...stateDropdown}
                        value={model.state}
                        onChange={v => setModel({...model, state: v!})}
                    />
                </fieldset>
                <FormActions />
            </Form>
        </Page>
    );
};

export default UserFormPage;
